import type { LoggableEvent } from '../recorder.ts'

export interface ConsoleTapOptions {
  console: Console
  window: Window
  log: (event: LoggableEvent) => void
  /** Counts are batched so a noisy loop becomes one entry with a count, not thousands. */
  flushMs?: number
}

type Level = 'error' | 'warn'
const DEFAULT_FLUSH_MS = 1000

/**
 * Counts console errors, warnings and uncaught window errors per level and source. The message
 * text is never read or stored, because it can carry customer data (EC30).
 */
export function startConsoleTap({ console: target, window, log, flushMs = DEFAULT_FLUSH_MS }: ConsoleTapOptions): () => void {
  const counts = new Map<string, { level: Level; source: string; count: number }>()
  let timer: ReturnType<typeof setTimeout> | undefined

  const flush = () => {
    clearTimeout(timer)
    timer = undefined
    for (const { level, source, count } of counts.values()) log({ type: 'console', level, source, count })
    counts.clear()
  }

  const bump = (level: Level, source: string) => {
    const key = `${level}:${source}`
    const entry = counts.get(key)
    if (entry) entry.count += 1
    else counts.set(key, { level, source, count: 1 })
    timer ??= setTimeout(flush, flushMs)
  }

  const originalError = target.error
  const originalWarn = target.warn
  target.error = function (this: unknown, ...args: unknown[]) {
    bump('error', 'console')
    return originalError.apply(target, args)
  }
  target.warn = function (this: unknown, ...args: unknown[]) {
    bump('warn', 'console')
    return originalWarn.apply(target, args)
  }
  const onWindowError = () => bump('error', 'window')
  window.addEventListener('error', onWindowError)

  return () => {
    flush()
    target.error = originalError
    target.warn = originalWarn
    window.removeEventListener('error', onWindowError)
  }
}
